import React, { useEffect, useMemo } from 'react';
import ToggleInput from '../../../components/ui/ToggleInput';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '../../../components/ui/Form';
import { Select, SelectItem } from '../../../components/ui/Select';
import { INTERCOM_UNIT_NAME_DISPLAY_SETTINGS, INTERCOM_NAME_SETTINGS } from '../../../helper/constants';
import { Flex } from '../../../components/ui/Flex';
import styled from 'styled-components';
import { Fonts, Palette } from '../../../components/utils';
import { IoChevronForward } from 'react-icons/io5';
import { Separator } from '../../../components/ui/Separator';

const PREVIEW_NAME = 'John Doe';
const PREVIEW_UNIT = 'Unit 101';

/**
 * @param {Object} props
 * @param {boolean} [props.showIntercomDisplaySettings=true] - When false (e.g. in Edit Building modal),
 *   only show Sync co-residents and Enable private pins. Intercom display settings live on the Intercom tab.
 */
const AkuvoxConfigInput = ({ showIntercomDisplaySettings = true }) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const [enableAkuvox, enableAkuvoxPrivatePins, nameSetting, nameDisplaySetting] = useWatch({
    control: form.control,
    name: ['enable_akuvox', 'enable_akuvox_private_pins', 'intercom_name_format', 'intercom_unit_name_display']
  });

  const canEnableEditIntercomPin = !!(enableAkuvox && enableAkuvoxPrivatePins);

  useEffect(() => {
    if (!canEnableEditIntercomPin) {
      form.setValue('enable_edit_intercom_pin', false);
    }
  }, [canEnableEditIntercomPin, form]);

  const previewName = useMemo(() => {
    if (!nameSetting || !nameDisplaySetting) return PREVIEW_NAME;
    const nameConfig = Object.values(INTERCOM_NAME_SETTINGS).find(config => config.value === nameSetting);
    const displayConfig = Object.values(INTERCOM_UNIT_NAME_DISPLAY_SETTINGS).find(config => config.value === nameDisplaySetting);

    if (!nameConfig || !displayConfig) return PREVIEW_NAME;

    const formattedName = nameConfig.preview(PREVIEW_NAME);
    return displayConfig.preview(formattedName, PREVIEW_UNIT);
  }, [nameSetting, nameDisplaySetting]);

  return (
    <>
      <ToggleInput
        form={form}
        name="enable_akuvox"
        label={t('Enable akuvox integration')}
        description={t(
          'Activates the akuvox intercom system integration. Only primary residents will be synced when enabled'
        )}
      />

      {enableAkuvox && (
        <>
          <ToggleInput
            form={form}
            name="sync_akuvox_co_residents"
            label={t('Sync co-residents to akuvox')}
            description={t('Also sync co-residents to akuvox in addition to primary residents')}
          />

          <ToggleInput
            form={form}
            name="enable_akuvox_private_pins"
            label={t('Enable akuvox private pins')}
            description={t(
              'Automatically assign a private door PIN to each synced resident or co-resident'
            )}
          />

          {canEnableEditIntercomPin && (
            <ToggleInput
              form={form}
              name="enable_edit_intercom_pin"
              label={t('Enable edit intercom pin')}
              description={t('Allow residents to edit their personal intercom PIN from the app')}
            />
          )}

          {showIntercomDisplaySettings && (
            <>
              <FormField
                control={form.control}
                name="intercom_name_format"
                render={({ field }) => (
                  <FormItem alt>
                    <FormLabel v3>{t('Resident name format')}</FormLabel>
                    <FormControl>
                      <Select placeholder={t('Select resident name format')} {...field}>
                        {Object.entries(INTERCOM_NAME_SETTINGS).map(([_, { value, label }]) => (
                          <SelectItem value={value} key={value}>
                            {t(label)}
                          </SelectItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Flex gap="0.125rem" alignItems="center">
                <IoChevronForward size={20} />
                <ExplanationText>{t('Controls whether the residents unit number is shown, and where it appears relative to the name.')}</ExplanationText>
              </Flex>

              <FormField
                control={form.control}
                name="intercom_unit_name_display"
                render={({ field }) => (
                  <FormItem alt>
                    <FormLabel v3>{t('Unit number display')}</FormLabel>
                    <FormControl>
                      <Select placeholder={t('Select unit number display')} {...field}>
                        {Object.entries(INTERCOM_UNIT_NAME_DISPLAY_SETTINGS).map(([_, { value, label }]) => (
                          <SelectItem value={value} key={value}>
                            {t(label)}
                          </SelectItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Flex gap="0.125rem" alignItems="center">
                <IoChevronForward size={20} />
                <ExplanationText>{t('Whether the unit number is displayed before or after the name.')}</ExplanationText>
              </Flex>

              <NameFormatPreview>
                <p>{t('Display name preview')}</p>
                <p>{previewName}</p>
              </NameFormatPreview>

              <Separator orientation="horizontal" />
            </>
          )}
        </>
      )}
    </>
  );
};

const ExplanationText = styled.p`
  margin-bottom: 0;
  font-size: 12px;
  color: ${Palette.HAZY_BLUE};
`;

const NameFormatPreview = styled.div`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 10px;
  background: ${Palette.SOLITUDE};

  p {
    color: ${Palette.BLACK};
    font-family: ${Fonts.OPEN_SANS};
    font-size: 14px;
    font-weight: 400;
    margin: 0;
  }

  p:last-child {
    font-weight: 700;
  }
`;


export default AkuvoxConfigInput;